/**
 * ColumnFilter.jsx — spreadsheet-style multi-select column filter.
 *
 * Extracted from the Trial Management table (IMP-1806's TrialColFilter in
 * InProgress.jsx, which still carries its own copy) so other tables — first
 * the Customers tab — can share the pattern: OR within a column, AND across
 * columns, all client-side over the loaded rows.
 *
 * Props:
 *   label    — column name, used in the trigger tooltip
 *   options  — [{ value, label, count?, heading? }]
 *   selected — Set of selected values
 *   onChange — (nextSet) => void
 *   compact  — render as the header-embedded funnel glyph (default full pill)
 */

import { useState, useEffect, useRef } from 'react';

// Fixed-position coords for a popup anchored to a button's rect. Tables live
// in overflow scroll containers that clip absolutely-positioned children, so
// popups render position:fixed instead — and flip above the anchor when the
// estimated height doesn't fit below the viewport edge.
export function fixedPopupPos(rect, { height = 200, width = 250, align = 'left' } = {}) {
  const top = rect.bottom + 4 + height > window.innerHeight
    ? Math.max(8, rect.top - 4 - height)
    : rect.bottom + 4;
  return align === 'right'
    ? { top, right: Math.max(8, window.innerWidth - rect.right) }
    : { top, left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)) };
}

export default function ColumnFilter({ label, options, selected, onChange, compact = false }) {
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    // The panel is position:fixed, so close it when anything else scrolls
    // (scrolls inside the panel's own option list are fine).
    function onScroll(e) { if (ref.current && ref.current.contains(e.target)) return; setOpen(false); }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const active = selected.size > 0;
  function toggleOpen(e) {
    setPos(fixedPopupPos(e.currentTarget.getBoundingClientRect(), { height: 300, width: 240 }));
    setOpen((v) => !v);
  }
  function toggle(v) {
    const n = new Set(selected);
    n.has(v) ? n.delete(v) : n.add(v);
    onChange(n);
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: compact ? 'inline-flex' : 'block' }}>
      {compact ? (
        // Header-embedded trigger (contacts-style): just the funnel glyph,
        // orange when the column has an active filter.
        <button
          onClick={toggleOpen}
          title={`Filter by ${label.toLowerCase()}${active ? ` (${selected.size} active)` : ''}`}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', lineHeight: 1,
            fontSize: 11, color: active ? 'var(--orange)' : 'var(--text-3)', fontWeight: active ? 800 : 400,
          }}
        >
          {active ? `▼${selected.size}` : '▽'}
        </button>
      ) : (
        <button
          onClick={toggleOpen}
          style={{
            fontSize: 11.5, fontWeight: 600, padding: '4px 9px', borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${active ? 'var(--orange)' : 'var(--border)'}`,
            background: active ? 'var(--orange-bg, #fff7ed)' : 'var(--surface)',
            color: active ? 'var(--orange)' : 'var(--text-3)', whiteSpace: 'nowrap',
          }}
        >
          {label}{active ? ` · ${selected.size}` : ''} <span style={{ fontSize: 9 }}>▾</span>
        </button>
      )}
      {open && (
        <div style={{
          position: 'fixed', top: pos?.top, left: pos?.left, zIndex: 9000, minWidth: 200, maxWidth: 280,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,.16)', padding: 6, maxHeight: 300, overflowY: 'auto',
          // Undo the th typography (uppercase/letter-spacing) when embedded in a header
          textTransform: 'none', letterSpacing: 'normal', fontWeight: 400, whiteSpace: 'normal',
        }}>
          {options.length === 0 && (
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', padding: '6px 8px' }}>No values in this view</div>
          )}
          {options.map(({ value, label: optLabel, count, heading }) => heading ? (
            <div key={value} style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.05em', padding: '6px 8px 2px' }}>
              {optLabel}
            </div>
          ) : (
            <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 8px', borderRadius: 5, cursor: 'pointer', fontSize: 12.5 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
              <input type="checkbox" checked={selected.has(value)} onChange={() => toggle(value)} style={{ cursor: 'pointer', margin: 0 }} />
              <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{optLabel}</span>
              {count != null && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{count}</span>}
            </label>
          ))}
          {active && (
            <button
              onClick={() => onChange(new Set())}
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '5px 8px', fontSize: 11.5, fontWeight: 600, color: 'var(--orange)', background: 'none', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left' }}
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
